/**
 * The programme's codes in words (usability audit 2026-10-02, A13). Each map is keyed on the codes
 * bata-platform v0.61.0 sends, named above it; a code a map does not know is shown as sent.
 */

import type { TFunction } from 'i18next';

import type { Polarity } from '@/export/types';

/**
 * Why a test-vs-control pair cannot be evaluated (`D4EvidenceRow.not_evaluable_reason_codes`): the
 * two D4 preconditions `services/jobs/exit.py` writes (`planning/exit/shadow.py`
 * `REASON_COVERAGE_BELOW_TARGET`, `REASON_BASELINE_UNGUARDED`).
 */
export const CONTROL_REASON_KEY: Readonly<Record<string, string>> = {
  elasticity_coverage_below_target: 'strategic.control.reason.elasticityCoverageBelowTarget',
  promotion_baseline_unguarded: 'strategic.control.reason.promotionBaselineUnguarded',
};

/**
 * What an override pair's projected value is (`OverridePairRow.projectionBasis`): the five
 * `services/repositories/programme_overrides.py` names, one per decision class.
 */
export const PROJECTION_BASIS_KEY: Readonly<Record<string, string>> = {
  'inseason_proposal.expected_impact': 'strategic.overrides.basis.proposalExpectedImpact',
  'inseason_transfer_proposal.net_benefit': 'strategic.overrides.basis.transferNetBenefit',
  'inseason_price_action.expected_margin_recovery': 'strategic.overrides.basis.priceExpectedMarginRecovery',
  'exit_consolidation_line.net_benefit_amount': 'strategic.overrides.basis.exitLaneNetBenefit',
  'exit_cadence_proposal.comparison_step_value': 'strategic.overrides.basis.exitCadenceStepValue',
};

/**
 * A programme measure (`MonthlyReportMeasurement.measure`): the measures
 * `services/repositories/programme.py` writes and `services/jobs/programme.py` declares.
 */
export const PROGRAMME_MEASURE_KEY: Readonly<Record<string, string>> = {
  availability_uplift: 'strategic.measure.availabilityUplift',
  recaptured_lost_value: 'strategic.measure.recapturedLostValue',
  realised_price_uplift: 'strategic.measure.realisedPriceUplift',
  seasonal_naive_wape: 'strategic.measure.seasonalNaiveWape',
  recommendation_override_rate: 'strategic.measure.recommendationOverrideRate',
  override_outcome_rate: 'strategic.measure.overrideOutcomeRate',
  cluster_membership_stability_rate: 'strategic.measure.clusterMembershipStabilityRate',
  net_sales_uplift: 'strategic.measure.netSalesUplift',
  // `markdown_test`'s end-of-period stock outcome — bata-platform ADR 9030, issue 524.
  terminal_units: 'strategic.measure.terminalUnits',
};

/**
 * Which way is better, for the readout measures — the uplifts of the pilot doors against their
 * matched controls (bata-platform ADR 9011, ADR 9022). A positive uplift is the pilot doing
 * better than its counterfactual. The other programme measures are not listed: their direction
 * is governance the platform has not published (an override rate is neutral by design, a
 * stability rate is not sufficiency), and this build does not guess it (`DeltaValue` has no
 * default polarity for the same reason).
 */
export const READOUT_MEASURE_POLARITY: Readonly<Record<string, Polarity>> = {
  availability_uplift: 'higher-is-better',
  net_sales_uplift: 'higher-is-better',
};

/** `EXCLUSION_MEASURE_SUFFIX` — an objective's exclusion companion row (`programme.py`). */
export const EXCLUSION_SUFFIX = '.excluded';

/** A measurement's unit: `currency`, `ratio`, and `subjects` (`UNIT_SUBJECTS`). */
export const PROGRAMME_UNIT_KEY: Readonly<Record<string, string>> = {
  currency: 'strategic.unit.currency',
  ratio: 'strategic.unit.ratio',
  subjects: 'strategic.unit.subjects',
};

type Translate = TFunction;

const lookup = (map: Readonly<Record<string, string>>, code: string): string | null => (
  Object.prototype.hasOwnProperty.call(map, code) ? map[code] ?? null : null
);

/** A code in words, or the code itself when the map has none. */
export function wordsOf(t: Translate, map: Readonly<Record<string, string>>, code: string): string {
  const key = lookup(map, code);
  return key === null ? code : t(key);
}

/** A programme measure in words; an exclusion companion row as its measure's exclusions. */
export function measureWords(t: Translate, measure: string): string {
  if (measure.endsWith(EXCLUSION_SUFFIX)) {
    const base = measure.slice(0, -EXCLUSION_SUFFIX.length);
    const key = lookup(PROGRAMME_MEASURE_KEY, base);
    return key === null ? measure : t('strategic.measure.excluded', { measure: t(key) });
  }
  return wordsOf(t, PROGRAMME_MEASURE_KEY, measure);
}
